import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Input } from "../../components/ui/input";
import { Checkbox } from "../../components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "../../components/ui/dialog";
import { Textarea } from "../../components/ui/textarea";
import { Label } from "../../components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import { 
  Search, Eye, FileText, Download, Mail, Trash2, 
  CheckSquare, Square, MoreHorizontal, RefreshCw, RotateCcw, AlertTriangle, Users, UserX
} from "lucide-react";
import { NestIcon } from "../../components/NestIcon";
import { AgentLayout } from "./AgentDashboard";
import { API } from "../../App";
import axios from "axios";
import { toast } from "sonner";

export default function AgentCustomers() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [customers, setCustomers] = useState([]);
  const [deletedCustomers, setDeletedCustomers] = useState([]);
  const [search, setSearch] = useState("");
  const [activeTab, setActiveTab] = useState("active");
  
  // Bulk selection state
  const [selectedIds, setSelectedIds] = useState([]);
  const [bulkActionLoading, setBulkActionLoading] = useState(false);
  
  // Email dialog state
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [emailSubject, setEmailSubject] = useState("");
  const [emailMessage, setEmailMessage] = useState("");
  
  // Delete confirmation dialog
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  
  // Permanent delete confirmation dialog
  const [permanentDeleteDialogOpen, setPermanentDeleteDialogOpen] = useState(false);
  const [customerToDelete, setCustomerToDelete] = useState(null);

  useEffect(() => {
    fetchCustomers();
    fetchDeletedCustomers();
  }, []);

  const fetchCustomers = async () => {
    const token = localStorage.getItem("agent_token");
    try {
      const response = await axios.get(`${API}/agent/customers`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCustomers(response.data);
    } catch (error) {
      toast.error("Failed to load customers");
    } finally {
      setLoading(false);
    }
  };

  const fetchDeletedCustomers = async () => {
    const token = localStorage.getItem("agent_token");
    try {
      const response = await axios.get(`${API}/agent/customers/deleted`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDeletedCustomers(response.data);
    } catch (error) {
      // May fail for non-admin users, that's OK
    }
  };

  const handleDeleteCustomer = async (userId) => {
    const token = localStorage.getItem("agent_token");
    try {
      await axios.delete(`${API}/agent/customers/${userId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Customer moved to deleted users");
      fetchCustomers();
      fetchDeletedCustomers();
      setSelectedIds([]);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete customer");
    }
  };

  const handleRestoreCustomer = async (userId) => {
    const token = localStorage.getItem("agent_token");
    try {
      await axios.post(`${API}/agent/customers/${userId}/restore`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Customer restored successfully");
      fetchCustomers();
      fetchDeletedCustomers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to restore customer");
    }
  };

  const handlePermanentDelete = async () => {
    if (!customerToDelete) return;
    
    const token = localStorage.getItem("agent_token");
    try {
      await axios.delete(`${API}/agent/customers/${customerToDelete.user_id}/permanent`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Customer permanently deleted");
      setPermanentDeleteDialogOpen(false);
      setCustomerToDelete(null);
      fetchDeletedCustomers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to permanently delete customer");
    }
  };

  const currentCustomers = activeTab === "active" ? customers : deletedCustomers;
  
  const filteredCustomers = currentCustomers.filter(customer => {
    if (!search) return true;
    const searchLower = search.toLowerCase();
    return (
      customer.user_id?.toLowerCase().includes(searchLower) ||
      customer.email?.toLowerCase().includes(searchLower) ||
      customer.full_name?.toLowerCase().includes(searchLower)
    );
  });

  // Bulk selection handlers
  const toggleSelectAll = () => {
    if (selectedIds.length === filteredCustomers.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(filteredCustomers.map(c => c.user_id));
    }
  };

  const toggleSelect = (userId) => {
    setSelectedIds(prev => 
      prev.includes(userId) 
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    );
  };

  const isAllSelected = filteredCustomers.length > 0 && selectedIds.length === filteredCustomers.length;
  const isSomeSelected = selectedIds.length > 0;

  // Bulk actions
  const handleBulkExport = async () => {
    if (selectedIds.length === 0) {
      toast.error("Please select customers to export");
      return;
    }
    
    setBulkActionLoading(true);
    const token = localStorage.getItem("agent_token");
    try {
      const response = await axios.post(
        `${API}/agent/customers/bulk`,
        { ids: selectedIds, action: "export" },
        { 
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob'
        }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `customers_export.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success(`Exported ${selectedIds.length} customers`);
      setSelectedIds([]);
    } catch (error) {
      toast.error("Failed to export customers");
    } finally {
      setBulkActionLoading(false);
    }
  };

  const handleBulkEmail = async () => {
    if (!emailSubject || !emailMessage) {
      toast.error("Please enter subject and message");
      return;
    }
    
    setBulkActionLoading(true);
    const token = localStorage.getItem("agent_token");
    try {
      const response = await axios.post(
        `${API}/agent/customers/bulk`,
        { 
          ids: selectedIds, 
          action: "send_email",
          params: { subject: emailSubject, message: emailMessage }
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(`Sent emails to ${response.data.success} customers`);
      if (response.data.failed > 0) {
        toast.warning(`Failed to send to ${response.data.failed} customers`);
      }
      setEmailDialogOpen(false);
      setEmailSubject("");
      setEmailMessage("");
      setSelectedIds([]);
    } catch (error) {
      toast.error("Failed to send emails");
    } finally {
      setBulkActionLoading(false);
    }
  };

  const handleBulkDelete = async () => {
    setBulkActionLoading(true);
    const token = localStorage.getItem("agent_token");
    try {
      const response = await axios.post(
        `${API}/agent/customers/bulk`,
        { ids: selectedIds, action: "delete" },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(`Moved ${response.data.success} customers to deleted`);
      setDeleteDialogOpen(false);
      setSelectedIds([]);
      fetchCustomers();
      fetchDeletedCustomers();
    } catch (error) {
      toast.error("Failed to delete customers");
    } finally {
      setBulkActionLoading(false);
    }
  };

  return (
    <AgentLayout title="Customers">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-foreground" style={{ fontFamily: 'Young Serif, serif' }}>
          Customer Management
        </h1>
        <p className="text-muted-foreground">View and manage customer accounts</p>
      </div>

      {/* Tabs for Active and Deleted Customers */}
      <Tabs value={activeTab} onValueChange={(value) => { setActiveTab(value); setSelectedIds([]); }} className="mb-6">
        <TabsList>
          <TabsTrigger value="active" className="flex items-center gap-2" data-testid="active-customers-tab">
            <Users className="h-4 w-4" />
            Active Customers
            <Badge variant="secondary" className="ml-1">{customers.length}</Badge>
          </TabsTrigger>
          <TabsTrigger value="deleted" className="flex items-center gap-2" data-testid="deleted-customers-tab">
            <UserX className="h-4 w-4" />
            Deleted Users
            <Badge variant="destructive" className="ml-1">{deletedCustomers.length}</Badge>
          </TabsTrigger>
        </TabsList>
      </Tabs>

      {/* Search and Bulk Actions Bar */}
      <div className="mb-6 flex flex-wrap gap-4 items-center justify-between">
        <div className="relative max-w-md flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search customers..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10"
            data-testid="customer-search-input"
          />
        </div>
        
        {/* Bulk Action Buttons */}
        {isSomeSelected && (
          <div className="flex items-center gap-2 bg-primary/5 px-4 py-2 rounded-lg border border-primary/20">
            <Badge variant="secondary" className="mr-2">
              {selectedIds.length} selected
            </Badge>
            <Button 
              size="sm" 
              variant="outline"
              onClick={handleBulkExport}
              disabled={bulkActionLoading}
              data-testid="bulk-export-btn"
            >
              <Download className="h-4 w-4 mr-1" />
              Export
            </Button>
            <Button 
              size="sm" 
              variant="outline"
              onClick={() => setEmailDialogOpen(true)}
              disabled={bulkActionLoading}
              data-testid="bulk-email-btn"
            >
              <Mail className="h-4 w-4 mr-1" />
              Email
            </Button>
            <Button 
              size="sm" 
              variant="destructive"
              onClick={() => setDeleteDialogOpen(true)}
              disabled={bulkActionLoading}
              data-testid="bulk-delete-btn"
            >
              <Trash2 className="h-4 w-4 mr-1" />
              Delete
            </Button>
          </div>
        )}
      </div>

      {/* Customers Table */}
      <Card className="card-organic overflow-hidden">
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
          ) : filteredCustomers.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="data-table">
                <thead>
                  <tr>
                    <th className="w-12">
                      <Checkbox
                        checked={isAllSelected}
                        onCheckedChange={toggleSelectAll}
                        data-testid="select-all-checkbox"
                      />
                    </th>
                    <th>Customer</th>
                    <th>Email</th>
                    <th>Policies</th>
                    <th>Claims</th>
                    <th>{activeTab === "deleted" ? "Deleted" : "Joined"}</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredCustomers.map((customer) => (
                    <tr key={customer.user_id} className={selectedIds.includes(customer.user_id) ? "bg-primary/5" : ""}>
                      <td>
                        <Checkbox
                          checked={selectedIds.includes(customer.user_id)}
                          onCheckedChange={() => toggleSelect(customer.user_id)}
                          data-testid={`select-${customer.user_id}`}
                        />
                      </td>
                      <td>
                        <div className="flex items-center gap-3">
                          <div className={`w-10 h-10 rounded-full flex items-center justify-center ${activeTab === "deleted" ? "bg-red-100" : "bg-primary/10"}`}>
                            <span className={activeTab === "deleted" ? "text-red-600 font-semibold" : "text-primary font-semibold"}>
                              {customer.full_name?.charAt(0) || customer.email?.charAt(0) || '?'}
                            </span>
                          </div>
                          <div>
                            <p className="font-medium">{customer.full_name || "N/A"}</p>
                            <p className="text-xs font-mono text-muted-foreground">{customer.user_id}</p>
                          </div>
                        </div>
                      </td>
                      <td>{customer.email}</td>
                      <td>
                        <div className="flex items-center gap-1">
                          <NestIcon className="h-4 w-4 text-primary" />
                          <span>{customer.policy_count || 0}</span>
                        </div>
                      </td>
                      <td>
                        <div className="flex items-center gap-1">
                          <FileText className="h-4 w-4 text-amber-600" />
                          <span>{customer.claim_count || 0}</span>
                        </div>
                      </td>
                      <td className="text-sm text-muted-foreground">
                        {activeTab === "deleted" 
                          ? (customer.deleted_at ? new Date(customer.deleted_at).toLocaleDateString() : "N/A")
                          : (customer.created_at ? new Date(customer.created_at).toLocaleDateString() : "N/A")
                        }
                      </td>
                      <td>
                        {activeTab === "active" ? (
                          <div className="flex items-center gap-2">
                            <Button 
                              size="sm" 
                              variant="outline"
                              onClick={() => navigate(`/agent/customers/${customer.user_id}`)}
                              data-testid={`view-customer-${customer.user_id}`}
                            >
                              <Eye className="h-4 w-4 mr-1" />
                              View
                            </Button>
                            <Button 
                              size="sm" 
                              variant="ghost"
                              className="text-red-600 hover:text-red-700 hover:bg-red-50"
                              onClick={() => handleDeleteCustomer(customer.user_id)}
                              data-testid={`delete-customer-${customer.user_id}`}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        ) : (
                          <div className="flex items-center gap-2">
                            <Button 
                              size="sm" 
                              variant="outline"
                              className="text-green-600 hover:text-green-700 hover:bg-green-50"
                              onClick={() => handleRestoreCustomer(customer.user_id)}
                              data-testid={`restore-customer-${customer.user_id}`}
                            >
                              <RotateCcw className="h-4 w-4 mr-1" />
                              Restore
                            </Button>
                            <Button 
                              size="sm" 
                              variant="destructive"
                              onClick={() => {
                                setCustomerToDelete(customer);
                                setPermanentDeleteDialogOpen(true);
                              }}
                              data-testid={`permanent-delete-${customer.user_id}`}
                            >
                              <AlertTriangle className="h-4 w-4 mr-1" />
                              Delete Forever
                            </Button>
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-12">
              <UserX className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <p className="text-muted-foreground">
                {activeTab === "deleted" ? "No deleted users" : "No customers found"}
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Email Dialog */}
      <Dialog open={emailDialogOpen} onOpenChange={setEmailDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Send Bulk Email</DialogTitle>
            <DialogDescription>
              Send an email to {selectedIds.length} selected customer(s)
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Subject</Label>
              <Input
                placeholder="Email subject"
                value={emailSubject}
                onChange={(e) => setEmailSubject(e.target.value)}
                data-testid="email-subject"
              />
            </div>
            <div>
              <Label>Message</Label>
              <Textarea
                placeholder="Enter your message..."
                value={emailMessage}
                onChange={(e) => setEmailMessage(e.target.value)}
                rows={5}
                data-testid="email-message"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEmailDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleBulkEmail} disabled={bulkActionLoading} data-testid="send-email-btn">
              {bulkActionLoading ? <RefreshCw className="h-4 w-4 mr-2 animate-spin" /> : <Mail className="h-4 w-4 mr-2" />}
              Send Email
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Customers?</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete {selectedIds.length} customer(s)? They will be moved to the "Deleted Users" tab and can be restored later.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
            <Button variant="destructive" onClick={handleBulkDelete} disabled={bulkActionLoading} data-testid="confirm-delete-btn">
              {bulkActionLoading ? <RefreshCw className="h-4 w-4 mr-2 animate-spin" /> : <Trash2 className="h-4 w-4 mr-2" />}
              Move to Deleted
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Permanent Delete Confirmation Dialog */}
      <Dialog open={permanentDeleteDialogOpen} onOpenChange={setPermanentDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="text-red-600 flex items-center gap-2">
              <AlertTriangle className="h-5 w-5" />
              Permanent Deletion
            </DialogTitle>
            <DialogDescription className="text-base">
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 mt-4">
                <p className="font-semibold text-red-800 mb-2">⚠️ This action is IRREVERSIBLE!</p>
                <p className="text-red-700 text-sm mb-3">
                  You are about to permanently delete <strong>{customerToDelete?.full_name || customerToDelete?.email}</strong> and ALL their data including:
                </p>
                <ul className="text-sm text-red-600 list-disc list-inside space-y-1">
                  <li>All quotes and policies</li>
                  <li>All claims and payments</li>
                  <li>All documents and beneficiaries</li>
                  <li>All wallet transactions</li>
                  <li>All support tickets</li>
                </ul>
              </div>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setPermanentDeleteDialogOpen(false)}>Cancel</Button>
            <Button variant="destructive" onClick={handlePermanentDelete} data-testid="confirm-permanent-delete-btn">
              <AlertTriangle className="h-4 w-4 mr-2" />
              Delete Forever
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AgentLayout>
  );
}
